import { useMemo, useState, type ReactNode } from 'react'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useDepartments, useHierarchyLevel } from '@/hooks/useHierarchy'
import { formatDistributionCenterName } from '@/utils/formatters'
import {
  buildExportRows,
  buildSeasonGroups,
  DC_GROUP_OPTIONS,
  MILESTONE_CLASSIFICATION_OPTIONS,
  MILESTONES_DISPLAY_OPTIONS,
  PO_OPTIONS,
  POG_GROUP_OPTIONS,
  SEASON_OPTIONS,
  SKU_OPTIONS,
  YEAR_EVENT_OPTIONS,
  type EventProductFlowFilterState,
} from './eventProductFlowMockData'
import { EventProductFlowTable } from './EventProductFlowTable'
import { ExportDetailedViewTable } from './ExportDetailedViewTable'

const ALL = '__all__'

const IMPORT_DOMESTIC_OPTIONS = ['Import', 'Domestic']
const INITIAL_REPLEN_RESET_OPTIONS = ['Initial', 'Replen', 'Reset']
const POG_COMPLETE_DATE_OPTIONS = ['Complete', 'Incomplete', 'Overdue']

function FilterField({
  label,
  children,
}: {
  label: string
  children: ReactNode
}) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </label>
      {children}
    </div>
  )
}

function SimpleSelect({
  value,
  onValueChange,
  options,
  loading,
  formatOption = option => option,
}: {
  value: string | null
  onValueChange: (v: string | null) => void
  options: string[]
  loading?: boolean
  formatOption?: (option: string) => string
}) {
  return (
    <Select
      value={value ?? ALL}
      onValueChange={v => onValueChange(v === ALL ? null : v)}
    >
      <SelectTrigger className="w-full bg-white">
        <SelectValue placeholder={loading ? 'Loading...' : 'All'} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL}>All</SelectItem>
        {options.map(o => (
          <SelectItem key={o} value={o}>
            {formatOption(o)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

function formatTimestamp(date: Date): string {
  return date.toLocaleString('en-US', {
    month: 'numeric',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  })
}

export function EventProductFlowMockup() {
  const [season, setSeason] = useState<string | null>(null)
  const [yearEvent, setYearEvent] = useState<string | null>(null)
  const [milestonesDisplay, setMilestonesDisplay] = useState<string | null>(
    null
  )
  const [milestoneClassification, setMilestoneClassification] = useState<
    string | null
  >(null)
  const [dcGroupDcName, setDcGroupDcName] = useState<string | null>(null)
  const [po, setPo] = useState<string | null>(null)
  const [department, setDepartment] = useState<string | null>(null)
  const [classSubclass, setClassSubclass] = useState<string | null>(null)
  const [importDomestic, setImportDomestic] = useState<string | null>(null)
  const [initialReplenReset, setInitialReplenReset] = useState<string | null>(
    null
  )
  const [pogGroupPogId, setPogGroupPogId] = useState<string | null>(null)
  const [sku, setSku] = useState<string | null>(null)
  const [pogCompleteDate, setPogCompleteDate] = useState<string | null>(null)

  const lastUpdate = useMemo(() => formatTimestamp(new Date()), [])

  const { data: departments, isLoading: departmentsLoading } = useDepartments()
  const { data: subclasses, isLoading: subclassesLoading } =
    useHierarchyLevel('SUBCLASS')

  const filters = useMemo<EventProductFlowFilterState>(
    () => ({
      season,
      yearEvent,
      milestonesDisplay,
      milestoneClassification,
      dcGroupDcName,
      po,
      department,
      classSubclass,
      importDomestic,
      initialReplenReset,
      pogGroupPogId,
      sku,
    }),
    [
      season,
      yearEvent,
      milestonesDisplay,
      milestoneClassification,
      dcGroupDcName,
      po,
      department,
      classSubclass,
      importDomestic,
      initialReplenReset,
      pogGroupPogId,
      sku,
    ]
  )

  const groups = useMemo(() => buildSeasonGroups(filters), [filters])
  const exportRows = useMemo(() => buildExportRows(filters), [filters])

  const departmentOptions = (departments ?? []).map(
    d => `${d.code} - ${d.name}`
  )
  const classSubclassOptions = (subclasses ?? []).map(
    s => `${s.code} - ${s.name}`
  )

  return (
    <div className="flex flex-col gap-4 bg-[#fafafa] p-6">
      {/* Top strip */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="max-w-2xl text-[11px] leading-tight text-muted-foreground">
            <p className="font-medium">
              Color Status Rules (completion % is measured in Sellable SKUs)
            </p>
            <p>
              <span className="font-semibold text-green-700">Green</span> →
              Milestone is ≥95% complete and ≤3 days delayed.
            </p>
            <p>
              <span className="font-semibold text-amber-700">Yellow</span> →
              Milestone is ≥90% complete and ≤7 days delayed or overdue.
            </p>
            <p>
              <span className="font-semibold text-rose-700">Red</span> →
              Milestone is overdue or significantly delayed.
            </p>
          </div>
        </div>
        <div className="shrink-0 text-right text-[11px] text-muted-foreground">
          <div>Last Updated</div>
          <div>{lastUpdate}</div>
        </div>
      </div>

      {/* Filter grid — 2 rows of 6 */}
      <div className="grid grid-cols-6 gap-4">
        <FilterField label="Season">
          <SimpleSelect
            value={season}
            onValueChange={setSeason}
            options={SEASON_OPTIONS}
          />
        </FilterField>
        <FilterField label="Year > Event">
          <SimpleSelect
            value={yearEvent}
            onValueChange={setYearEvent}
            options={YEAR_EVENT_OPTIONS}
          />
        </FilterField>
        <FilterField label="Milestones Display">
          <SimpleSelect
            value={milestonesDisplay}
            onValueChange={setMilestonesDisplay}
            options={MILESTONES_DISPLAY_OPTIONS}
          />
        </FilterField>
        <FilterField label="Milestone Classification">
          <SimpleSelect
            value={milestoneClassification}
            onValueChange={setMilestoneClassification}
            options={MILESTONE_CLASSIFICATION_OPTIONS}
          />
        </FilterField>
        <FilterField label="DC Group > DC Name">
          <SimpleSelect
            value={dcGroupDcName}
            onValueChange={setDcGroupDcName}
            options={DC_GROUP_OPTIONS}
            formatOption={formatDistributionCenterName}
          />
        </FilterField>
        <FilterField label="PO">
          <SimpleSelect value={po} onValueChange={setPo} options={PO_OPTIONS} />
        </FilterField>

        <FilterField label="Department">
          <SimpleSelect
            value={department}
            onValueChange={setDepartment}
            options={departmentOptions}
            loading={departmentsLoading}
          />
        </FilterField>
        <FilterField label="Class > Subclass">
          <SimpleSelect
            value={classSubclass}
            onValueChange={setClassSubclass}
            options={classSubclassOptions}
            loading={subclassesLoading}
          />
        </FilterField>
        <FilterField label="Import / Domestic">
          <SimpleSelect
            value={importDomestic}
            onValueChange={setImportDomestic}
            options={IMPORT_DOMESTIC_OPTIONS}
          />
        </FilterField>
        <FilterField label="Initial / Replen / Reset">
          <SimpleSelect
            value={initialReplenReset}
            onValueChange={setInitialReplenReset}
            options={INITIAL_REPLEN_RESET_OPTIONS}
          />
        </FilterField>
        <FilterField label="POG Group > POG ID">
          <SimpleSelect
            value={pogGroupPogId}
            onValueChange={setPogGroupPogId}
            options={POG_GROUP_OPTIONS}
          />
        </FilterField>
        <FilterField label="SKU">
          <SimpleSelect
            value={sku}
            onValueChange={setSku}
            options={SKU_OPTIONS}
          />
        </FilterField>
      </div>

      <Tabs defaultValue="event-product-flow" className="mt-2">
        <TabsList>
          <TabsTrigger value="event-product-flow">
            Event Product Flow
          </TabsTrigger>
          <TabsTrigger value="export-view">Export View</TabsTrigger>
        </TabsList>

        <TabsContent value="event-product-flow">
          {groups.length === 0 ? (
            <div className="flex min-h-40 items-center justify-center rounded-lg border border-border bg-white text-sm text-muted-foreground">
              No results for the selected filters.
            </div>
          ) : (
            <EventProductFlowTable groups={groups} />
          )}
        </TabsContent>

        <TabsContent value="export-view">
          <div className="mb-3 flex justify-end">
            <div className="w-56">
              <FilterField label="POG Complete Date">
                <SimpleSelect
                  value={pogCompleteDate}
                  onValueChange={setPogCompleteDate}
                  options={POG_COMPLETE_DATE_OPTIONS}
                />
              </FilterField>
            </div>
          </div>
          {exportRows.length === 0 ? (
            <div className="flex min-h-40 items-center justify-center rounded-lg border border-border bg-white text-sm text-muted-foreground">
              No results for the selected filters.
            </div>
          ) : (
            <ExportDetailedViewTable rows={exportRows} />
          )}
        </TabsContent>
      </Tabs>
    </div>
  )
}
